import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './MessageActions.css';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');

const MessageActions = ({ 
  message, 
  onReply,
  onReplyInThread, 
  onEditMessage, 
  onDeleteMessage,
  onForwardMessage,
  onPinMessage,
  currentUser,
  threadCount = 0,
  roomId,
  roomType,
  token,
  isAdmin = false,
  isPinned = false
}) => {
  // Debug: Log the props received
  console.log('📌 [MessageActions] Props received:', {
    roomType,
    isAdmin,
    isPinned,
    roomId,
    hasToken: !!token,
    isDirectMessage: roomType === 'direct',
    buttonDisabled: roomType !== 'direct' && !isAdmin
  });
  
  // ALERT: Show current state for debugging
  if (roomType === 'direct') {
    console.log('🚨 [DM DEBUG] This is a DM - pin should be enabled for everyone!', {
      roomType,
      isAdmin,
      finalCanPin: roomType === 'direct' ? true : (isAdmin || false)
    });
  }
  
  // FORCE ENABLE PINNING IN DMs - BYPASS ALL ADMIN CHECKS
  const isDirectMessage = roomType === 'direct';
  const canPinInGroup = isAdmin;
  
  // In DMs, ALWAYS allow pinning regardless of admin status
  const canPin = isDirectMessage || canPinInGroup;
  
  // FORCE OVERRIDE: If it's a DM, always allow pinning
  const finalCanPin = isDirectMessage ? true : canPin;
  
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef(null);
  const menuBtnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, placement: 'above' });

  // Guard against accidental form submissions from any enclosing forms
  const withNoSubmit = (handler) => {
    return (event) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      handler && handler(event);
    };
  };

  // Close actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const btn = menuBtnRef.current;
      const dd = dropdownRef.current;
      if (dd && dd.contains(event.target)) return;
      if (btn && btn.contains(event.target)) return;
      if (actionsRef.current && actionsRef.current.contains(event.target)) return;
      setShowActions(false);
    };

    const handleReposition = () => {
      if (!showActions) return;
      const btn = menuBtnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current?.offsetWidth || 220;
      const dropdownHeight = dropdownRef.current?.offsetHeight || 260;
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;
      const canPlaceRight = spaceRight >= dropdownWidth + 12;
      const canPlaceLeft = spaceLeft >= dropdownWidth + 12;

      // Prefer side placement so we don't cover the message
      if (canPlaceRight || canPlaceLeft) {
        const left = canPlaceRight
          ? Math.min(rect.right + 8, window.innerWidth - dropdownWidth - 8)
          : Math.max(8, rect.left - dropdownWidth - 8);
        const top = Math.min(
          Math.max(8, rect.top - Math.max(0, (dropdownHeight - rect.height) / 2)),
          window.innerHeight - dropdownHeight - 8
        );
        setDropdownPos({ top, left, placement: canPlaceRight ? 'right' : 'left' });
        return;
      }

      // Fallback: above or below
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceAbove > dropdownHeight || spaceAbove > spaceBelow;
      const top = placeAbove
        ? Math.max(8, rect.top - dropdownHeight - 8)
        : Math.min(window.innerHeight - dropdownHeight - 8, rect.bottom + 8);
      const left = Math.min(Math.max(8, rect.right - dropdownWidth), window.innerWidth - dropdownWidth - 8);
      setDropdownPos({ top, left, placement: placeAbove ? 'above' : 'below' });
    };

    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      // Position after mount
      setTimeout(handleReposition, 0);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [showActions]);

  // Check if current user can edit/delete this message
  const canEditDelete = currentUser && (
    currentUser.id === message.sender?._id || 
    currentUser.id === message.sender?.id ||
    currentUser.role === 'admin' || 
    currentUser.role === 'super'
  );

  // Check if message can be deleted (own message within 30 minutes for non-admins, or admin anytime)
  const canDelete = currentUser && (
    // Admins can delete anytime
    (currentUser.role === 'admin' || currentUser.role === 'super') ||
    // Message sender can delete within 30 minutes
    ((currentUser.id === message.sender?._id || currentUser.id === message.sender?.id) &&
     isWithinEditWindow(message.createdAt))
  );

  // Check if message can be edited (own message within 30 minutes, not forwarded)
  const canEdit = currentUser && (
    (currentUser.id === message.sender?._id || currentUser.id === message.sender?.id) &&
    isWithinEditWindow(message.createdAt) &&
    !message.meta?.isForwarded
  );

  // Helper function to check if message is within 30-minute edit window
  function isWithinEditWindow(createdAt) {
    const messageTime = new Date(createdAt).getTime();
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
    return (now - messageTime) <= thirtyMinutes;
  }

  // Get time remaining for editing
  function getEditTimeRemaining(createdAt) {
    const messageTime = new Date(createdAt).getTime();
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000;
    const timeElapsed = now - messageTime;
    const timeRemaining = thirtyMinutes - timeElapsed;
    
    if (timeRemaining <= 0) return null;
    
    const minutes = Math.floor(timeRemaining / (60 * 1000));
    const seconds = Math.floor((timeRemaining % (60 * 1000)) / 1000);
    return `${minutes}m ${seconds}s`;
  }

  // Debug logging
  console.log('MessageActions Debug:', {
    isAdmin,
    currentUser: currentUser?.name,
    userRole: currentUser?.role,
    messageId: message._id,
    isPinned,
    roomId,
    hasToken: !!token
  });

  const handleReply = () => {
    if (onReply) {
      onReply(message);
    }
    setShowActions(false);
  };

  const handleReplyInThread = () => {
    onReplyInThread(message);
    setShowActions(false);
  };

  const handleEditMessage = () => {
    onEditMessage(message);
    setShowActions(false);
  };

  const handleDeleteMessage = (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
      event.stopPropagation();
    }
    onDeleteMessage(message);
    setShowActions(false);
  };

  const handleForwardMessage = () => {
    onForwardMessage(message);
    setShowActions(false);
  };

  const handlePinMessage = async () => {
    console.log('📌 [PIN] handlePinMessage called:', {
      messageId: message._id,
      roomId,
      roomType,
      isAdmin,
      isPinned,
      hasToken: !!token,
      isDirectMessage,
      canPinInGroup,
      canPin,
      finalCanPin
    });
    
    if (!roomId || !token || !finalCanPin) {
      console.log('📌 [PIN] Cannot pin - missing requirements:', { roomId: !!roomId, token: !!token, finalCanPin, isDirectMessage });
      return;
    }
    
    try {
      const url = `${API_BASE}/api/chat/rooms/${roomId}/messages/${message._id}/pin`;
      const method = isPinned ? 'DELETE' : 'POST';
      
      console.log('📌 [PIN] Making request:', { 
        url, 
        method, 
        isDirectMessage, 
        finalCanPin,
        roomType,
        isAdmin 
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📌 [PIN] Response:', response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('📌 [PIN] Error response:', errorData);
        throw new Error(errorData.message || `Failed to ${isPinned ? 'unpin' : 'pin'} message`);
      }

      console.log('📌 [PIN] Success! Calling onPinMessage');
      
      // Call parent handler if provided
      if (onPinMessage) {
        onPinMessage(message);
      }
    } catch (error) {
      console.error('📌 [PIN] Error toggling pin:', error);
      // Optionally show error notification
    }
    
    setShowActions(false);
  };

  return (
    <div className="message-actions" ref={actionsRef}>
      {/* Thread Reply Count - Display only, not clickable (replies shown inline) */}
      {threadCount > 0 && (
        <span 
          className="thread-count-badge"
          title={`${threadCount} ${threadCount === 1 ? 'reply' : 'replies'}`}
        >
          💬 {threadCount}
        </span>
      )}

      {/* Actions Menu Button */}
      <button 
        className={`actions-menu-btn ${showActions ? 'active' : ''}`}
        onClick={withNoSubmit(() => setShowActions(!showActions))}
        title="Message actions"
        ref={menuBtnRef}
        type="button"
        onMouseDown={(e) => { e.preventDefault(); }}
      >
        ⋯
      </button>

      {/* Actions Dropdown rendered in a portal (prevents clipping) */}
      {showActions && createPortal(
        (
          <div
            className="actions-dropdown-portal"
            ref={dropdownRef}
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}
          >
            <button onClick={withNoSubmit(handleReply)} className="action-item" type="button">
              <span className="action-icon">↩️</span>
              Reply
            </button>
            
            <button onClick={withNoSubmit(handleForwardMessage)} className="action-item" type="button">
              <span className="action-icon">↗️</span>
              Forward message
            </button>
            
            <button 
              onClick={withNoSubmit(handlePinMessage)} 
              className={`action-item ${!finalCanPin ? 'disabled' : ''}`}
              type="button"
              disabled={!finalCanPin}
              title={`Pin message - ${isDirectMessage ? 'Anyone can pin in DMs' : (canPinInGroup ? 'You can pin as admin' : 'Only admins can pin')}`}
            >
              <span className="action-icon">{isPinned ? '📌' : '📌'}</span>
              {isPinned ? 'Unpin message' : 'Pin message'}
            </button>
            
            {(canEdit || canEditDelete) && (
              <>
                <div className="action-separator"></div>
                <button 
                  onClick={withNoSubmit(handleEditMessage)} 
                  className={`action-item ${!canEdit ? 'disabled' : ''}`}
                  type="button"
                  disabled={!canEdit}
                  title={
                    canEdit 
                      ? `Edit message (${getEditTimeRemaining(message.createdAt)} remaining)`
                      : message.meta?.isForwarded
                        ? 'Forwarded messages cannot be edited'
                      : currentUser && (currentUser.id === message.sender?._id || currentUser.id === message.sender?.id)
                        ? 'You can no longer edit this message (30 minute limit expired)'
                        : 'You can only edit your own messages'
                  }
                >
                  <span className="action-icon">✏️</span>
                  Edit message
                  {!canEdit && currentUser && (currentUser.id === message.sender?._id || currentUser.id === message.sender?.id) && (
                    <span className="edit-expired">
                      {message.meta?.isForwarded ? ' (Forwarded)' : ' (Expired)'}
                    </span>
                  )}
                </button>
                
                {canEditDelete && (
                  <button 
                    onClick={withNoSubmit(handleDeleteMessage)} 
                    className={`action-item danger ${!canDelete ? 'disabled' : ''}`}
                    type="button"
                    disabled={!canDelete}
                    title={
                      canDelete 
                        ? `Delete message${(currentUser.id === message.sender?._id || currentUser.id === message.sender?.id) && !(currentUser.role === 'admin' || currentUser.role === 'super') ? ` (${getEditTimeRemaining(message.createdAt)} remaining)` : ''}`
                        : currentUser && (currentUser.id === message.sender?._id || currentUser.id === message.sender?.id)
                          ? 'You can no longer delete this message (30 minute limit expired)'
                          : 'You can only delete your own messages'
                    }
                  >
                    <span className="action-icon">🗑️</span>
                    Delete message
                    {!canDelete && currentUser && (currentUser.id === message.sender?._id || currentUser.id === message.sender?.id) && (
                      <span className="edit-expired"> (Expired)</span>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default MessageActions;

